import { assign, createMachine } from "xstate";
import { Settings2FAContext } from "./types/Settings2FAContext";
import { Settings2FAEvent } from "./types/Settings2FAEvent";
import { Settings2FAState } from "./types/Settings2FAState";
import { enable2FA } from "./services/enable2FA";
import { disable2FA } from "./services/disable2FA";
import { fetch2FAStatus } from "./services/fetch2FAStatus";
import { fetchSecret } from "./services/fetchSecret";
import { set2FAEnabledAction } from "./actions/set2FAEnabledAction";
import { set2FADisabledAction } from "./actions/set2FADisabledAction";
import { set2FAStatusAction } from "./actions/set2FAStatusAction";
import { setSecretAction } from "./actions/setSecretAction";
import { setSetupSuccessMessageAction } from "./actions/setSetupSuccessMessageAction";
import { setDisableSuccessMessageAction } from "./actions/setDisableSuccessMessageAction";
import { setErrorMessageAction } from "./actions/setErrorMessageAction";
import { clearMessageAction } from "./actions/clearMessageAction";

export const SETTINGS_2FA_STATE_MACHINE_ID = "settings2FASM";

export const settings2FASM = createMachine<
  Settings2FAContext,
  Settings2FAEvent,
  Settings2FAState
>(
  {
    id: SETTINGS_2FA_STATE_MACHINE_ID,
    initial: "loading",
    context: {
      userId: undefined,
      is2FAEnabled: false,
    },
    states: {
      loading: {
        invoke: {
          id: "fetch2FAStatus",
          src: fetch2FAStatus,
        },
        on: {
          STATUS_LOADED: {
            target: "idle",
            actions: "set2FAStatus",
          },
          ERROR: {
            target: "idle",
            actions: "setErrorMessage",
          },
        },
      },
      idle: {
        on: {
          TOGGLE: [
            {
              target: "loadSecret",
              actions: "clearMessage",
              cond: "is2FADisabled",
            },
            {
              target: "disable",
              actions: "clearMessage",
              cond: "is2FAEnabled",
            },
          ],
        },
      },
      loadSecret: {
        invoke: {
          id: "fetchSecret",
          src: fetchSecret,
        },
        on: {
          SECRET_FETCHED: {
            target: "setup",
            actions: "setSecret",
          },
          ERROR: {
            target: "idle",
            actions: "setErrorMessage",
          },
        },
      },
      setup: {
        on: {
          SUBMIT_CODE: {
            target: "enabling2FA",
            actions: ["clearMessage"],
          },
          CANCEL: {
            target: "idle",
          },
        },
      },
      enabling2FA: {
        invoke: {
          id: "enable2FA",
          src: enable2FA,
        },
        on: {
          ENABLE_SUCCESS: {
            target: "idle",
            actions: ["set2FAEnabled", "setSetupSuccessMessage"],
          },
          ERROR: {
            target: "setup",
            actions: "setErrorMessage",
          },
        },
      },
      disable: {
        on: {
          SUBMIT_CODE: {
            target: "disabling2FA",
            actions: ["clearMessage"],
          },
          CANCEL: {
            target: "idle",
          },
        },
      },
      disabling2FA: {
        invoke: {
          id: "disable2FA",
          src: disable2FA,
        },
        on: {
          DISABLE_SUCCESS: {
            target: "idle",
            actions: ["set2FADisabled", "setDisableSuccessMessage"],
          },
          ERROR: {
            target: "disable",
            actions: "setErrorMessage",
          },
        },
      },
    },
  },
  {
    guards: {
      is2FADisabled: (context) => !context.is2FAEnabled,
      is2FAEnabled: (context) => context.is2FAEnabled,
    },
    actions: {
      set2FAStatus: assign(set2FAStatusAction),
      set2FAEnabled: assign(set2FAEnabledAction),
      set2FADisabled: assign(set2FADisabledAction),
      setSecret: assign(setSecretAction),
      setSetupSuccessMessage: assign(setSetupSuccessMessageAction),
      setDisableSuccessMessage: assign(setDisableSuccessMessageAction),
      setErrorMessage: assign(setErrorMessageAction),
      clearMessage: assign(clearMessageAction),
    },
  }
);
